import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { GlobalMessageService, GlobalMessageType, TranslatePipe } from '@spartacus/core';
import { MediaComponent } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import {
  ChangeOrderEntriesPayload,
  ChangeOrderPayload,
  OrderDetailsEnum,
  modalTranslatables,
} from 'src/app/model/order-details.model';
import { ComponentService } from 'src/app/service/component.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { OrderDetailsService } from 'src/app/service/myaccount/order-details.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { RemoveImageTitleAttrDirective } from '../../../../../shared/remove-title-attr.directive';

@Component({
  selector: 'app-cancel-edit-order-modal',
  templateUrl: './cancel-edit-order-modal.component.html',
  styleUrls: ['./cancel-edit-order-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgClass,
    MatIcon,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    MediaComponent,
    RemoveImageTitleAttrDirective,
    MatError,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class CancelEditOrderModalComponent implements OnInit {
  cancelEditForm: FormGroup;
  translatables: modalTranslatables;
  modalType: string;
  data: any;
  order: any;
  cancelWholeOrder: boolean;
  changeOrderEntries: any;
  cancelOrderReasons$: Observable<any>;
  isLoading: boolean;
  srpSubscription$: Subscription;
  showSrp = false;
  erpOrderType: string;
  businessType: any;
  messageSubscription$: Subscription;

  constructor(
    private modalService: StandardModalService,
    private formBuilder: FormBuilder,
    private orderDetailsService: OrderDetailsService,
    private messageService: GlobalMessageService,
    private storageService: StorageService,
    private componentService: ComponentService,
    private currentBusinessType: CurrentBusinessType
  ) {
    this.modalService
      .getModalData()
      .pipe(take(1))
      .subscribe((res) => {
        (this.modalType = res.modalType),
          (this.translatables = res.translatables),
          (this.data = res.data),
          (this.changeOrderEntries = res.changeOrderEntries),
          (this.order = res.order),
          (this.erpOrderType = res.erpOrderType);
      });
  }

  get cancellationReason() {
    return this.cancelEditForm.get('cancellationReason');
  }

  get qty() {
    return this.cancelEditForm.get('qty');
  }

  get isCancelItems(): boolean {
    return (
      this.modalType === OrderDetailsEnum.CANCEL_SELECTED_ITEMS_MODAL ||
      this.modalType === OrderDetailsEnum.CANCEL_WHOLE_ORDER_MODAL
    );
  }

  get isEditQty(): boolean {
    return this.modalType === OrderDetailsEnum.EDIT_ITEM_QTY_MODAL;
  }

  ngOnInit(): void {
    this.businessType = this.currentBusinessType.getBusinessType();
    this.buildForm();
    this.onCancelEditFormValueChange();
    this.getInitSrpToggleValue();
    if (this.isCancelItems) {
      this.cancelOrderReasons$ = this.orderDetailsService.getCancelOrderReasons();
      this.cancelWholeOrder = this.modalType === OrderDetailsEnum.CANCEL_WHOLE_ORDER_MODAL;
    }
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  buildForm(): void {
    if (this.isCancelItems) {
      this.cancelEditForm = this.formBuilder.group({
        cancellationReason: [null, Validators.required],
      });
    } else if (this.isEditQty) {
      this.cancelEditForm = this.formBuilder.group({
        qty: [
          this.data.qty,
          [Validators.required, Validators.max(999), Validators.min(0), this.checkQuantity.bind(this)],
        ],
      });
    }
  }

  isPaymentCC(): boolean {
    return this.order.paymentInfoDetails.cardType;
  }

  checkQuantity(control: any) {
    const originalQty = this.data.qty;
    const userInputQty = parseInt(control.value);
    if (originalQty < userInputQty && this.isPaymentCC()) {
      return { quantityExceed: true };
    } else {
      return null;
    }
  }

  onInput(control: any) {
    if (control.value > 999) {
      control.setValue(999);
    }
  }

  onCancelEditFormValueChange(): void {
    const initialValue = this.cancelEditForm.value;
    this.cancelEditForm.valueChanges
      .pipe(
        map(() => Object.keys(initialValue).some((key) => this.cancelEditForm.value[key] != initialValue[key])),
        distinctUntilChanged()
      )
      .subscribe((valueChanged) => {
        // add form error if values not changed from initial values
        if (!valueChanged) {
          this.cancelEditForm.setErrors({ valueUnchanged: true });
        } else {
          this.cancelEditForm.setErrors({ valueUnchanged: false });
          this.cancelEditForm.updateValueAndValidity();
        }
      });
  }

  onSubmit(): void {
    let payload: ChangeOrderPayload;
    payload = {
      changeOrderType: this.cancelWholeOrder ? OrderDetailsEnum.CANCEL_ORDER : OrderDetailsEnum.CHANGE_ORDER,
      entries: this.cancelWholeOrder ? [] : this.getChangeOrderEntries(this.changeOrderEntries),
      erpOrderNumber: this.order.erpOrderNumber,
      reasonCode: this.isCancelItems ? this.cancellationReason?.value : undefined,
      shipTo: this.order.consignments[0].shippingAddress.addressId,
      erpOrderType: this.erpOrderType,
    };
    this.isLoading = true;
    this.orderDetailsService.submitChangeOrder(payload).subscribe(
      (success) => {
        if (success) {
          let key: string;
          this.isLoading = false;
          if (this.isCancelItems) {
            key = this.cancelWholeOrder
              ? 'myAccount.orderDetails.changeOrder.cancelWholeOrderSuccess'
              : 'myAccount.orderDetails.changeOrder.cancelItemsSuccess';
          } else if (this.isEditQty) {
            key = 'myAccount.orderDetails.changeOrder.editQtySuccess';
          }
          this.showGlobalMessage(key, this.order.erpOrderNumber, GlobalMessageType.MSG_TYPE_CONFIRMATION);
          this.closeModal('success');
          this.componentService.reloadOrderDetails$.next(true);
        }
      },
      (error) => {
        let key: string;
        this.isLoading = true;
        if (this.isCancelItems) {
          key = 'myAccount.orderDetails.changeOrder.cancelEditError';
        } else if (this.isEditQty) {
          key = 'myAccount.orderDetails.changeOrder.cancelEditError';
        }
        this.messageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
        this.showGlobalMessage(key, this.order.erpOrderNumber, GlobalMessageType.MSG_TYPE_ERROR);
        this.messageSubscription$ = this.messageService.get().subscribe((value) => {
          if (value['[GlobalMessage] Error'].length === 0) {
            this.messageSubscription$?.unsubscribe();
            window.location.reload();
          }
        });
      }
    );
  }

  getChangeOrderEntries(entries: any): ChangeOrderEntriesPayload[] {
    return entries.map((entry: any) => {
      if (this.isCancelItems) {
        return {
          changeLineType: OrderDetailsEnum.CANCEL_LINE,
          erpLineNumber: entry.erpLineNumber,
          modifiedQuantity: '',
          originalQuantity: '',
          skuCode: entry.product.code,
        };
      } else if (this.isEditQty) {
        return {
          changeLineType: this.qty.value > this.data.qty ? OrderDetailsEnum.ADD_QTY : OrderDetailsEnum.SUBTRACT_QTY,
          erpLineNumber: entry.erpLineNumber,
          modifiedQuantity: Math.abs(parseInt(this.qty.value) - parseInt(this.data.qty)).toString(), // value to be added/subtracted
          originalQuantity: this.data.qty.toString(),
          skuCode: entry.product.code,
        };
      }
    });
  }

  showGlobalMessage(text: string, param: string, messageType: GlobalMessageType) {
    this.messageService.add({ key: text, params: { orderNum: param } }, messageType);
  }

  closeModal(reason: any): void {
    this.modalService.closeDialog(reason);
  }
}
